import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSpinner,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";
import { Film } from "lucide-react";
import QuestionForm from "../CreatePresentation/QuestionForm";
import ExportPresentation from "../CreatePresentation/ExportPresentation";

const EditRightPanel = ({
  isReorderOnly,
  activeTab,
  setActiveTab,
  activeQuestionTab,
  setActiveQuestionTab,
  handleAddSlide,
  activeSlide,
  slides,
  setSlides,
  animatedSlides,
  setAnimatedSlides,
  isCurrentSlideAnimated,
  setIsAnimateMode,
  slidesAnimationData,
  setSlidesAnimationData,
  handleUpdatePresentation,
  updatePresentationLoading,
  getLinks,
}) => {
  return (
    <div
      className="col-span-3 bg-white rounded-2xl border-l border-gray-200 p-4 overflow-y-auto h-[38rem] flex flex-col justify-between"
      style={{ scrollbarWidth: "thin" }}
    >
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          {isReorderOnly ? "Slide Properties" : "Component Library"}
        </h2>

        <div className="flex space-x-2 text-sm text-gray-600 mb-4">
          {!isReorderOnly && (
            <button
              className={`px-3 py-1 rounded-lg ${
                activeTab === "interactive"
                  ? "bg-lightPurple/10 text-lightPurple"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => setActiveTab("interactive")}
            >
              Interactive Questions
            </button>
          )}

          <button
            className={`px-3 py-1 rounded-lg ${
              activeTab === "content" || isReorderOnly
                ? "bg-lightPurple/10 text-lightPurple"
                : "hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("content")}
          >
            {isReorderOnly ? "Settings" : "Content"}
          </button>
        </div>

        {activeTab === "interactive" && !isReorderOnly && (
          <div className="space-y-6 pl-5">
            <div className="flex space-x-2 mb-4 text-sm">
              {["poll", "multiple", "text"].map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveQuestionTab(type)}
                  className={`px-3 py-1 rounded-lg capitalize transition ${
                    activeQuestionTab === type
                      ? "bg-lightPurple text-white"
                      : "bg-gray-100 hover:bg-lightPurple/20 text-gray-700"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            {activeQuestionTab === "poll" && (
              <QuestionForm
                type="poll"
                label="Poll Question"
                placeholder="Enter Poll question"
                onSave={handleAddSlide}
              />
            )}

            {activeQuestionTab === "multiple" && (
              <QuestionForm
                type="multiple"
                label="Multiple Question"
                placeholder="Enter Multiple question"
                onSave={handleAddSlide}
              />
            )}

            {activeQuestionTab === "text" && (
              <QuestionForm
                type="text"
                label="Text Question"
                placeholder="Enter Text question"
                onSave={handleAddSlide}
              />
            )}
          </div>
        )}

        {activeTab === "content" && (
          <div className="space-y-6 pl-5">
            {activeSlide !== null && slides[activeSlide] && (
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Slide Settings
                  </h2>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-bold uppercase ${slides[activeSlide].is_active ? "text-green-600" : "text-gray-400"}`}
                    >
                      {slides[activeSlide].is_active ? "Active" : "Inactive"}
                    </span>
                    <button
                      onClick={() => {
                        const updatedSlides = [...slides];
                        updatedSlides[activeSlide].is_active =
                          !updatedSlides[activeSlide].is_active;
                        setSlides(updatedSlides);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        slides[activeSlide].is_active
                          ? "bg-lightPurple"
                          : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          slides[activeSlide].is_active
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Animated Slide Toggle — for image and question slides */}
                {(slides[activeSlide]?.type === "image" ||
                  slides[activeSlide]?.type === "question") && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        Animated Slide
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {slides[activeSlide]?.type === "image"
                          ? "Use slide image as canvas background"
                          : "Add animated overlay to question"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-bold uppercase ${isCurrentSlideAnimated ? "text-purpleColor" : "text-gray-400"}`}
                      >
                        {isCurrentSlideAnimated ? "On" : "Off"}
                      </span>
                      <button
                        onClick={() => {
                          const next = !animatedSlides[activeSlide];
                          setAnimatedSlides((prev) => ({
                            ...prev,
                            [activeSlide]: next,
                          }));
                          if (next) setIsAnimateMode(true);
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          isCurrentSlideAnimated
                            ? "bg-purpleColor"
                            : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            isCurrentSlideAnimated
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* Open Animation Editor button — shown when slide is marked animated */}
                {isCurrentSlideAnimated && (
                  <button
                    onClick={() => setIsAnimateMode(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purpleColor hover:bg-[#7a1b51] text-white text-sm font-semibold rounded-xl shadow transition"
                  >
                    <Film className="w-4 h-4" />
                    Open Animation Editor
                  </button>
                )}

                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                  Slide Content : {activeSlide + 1}
                </h3>

                {slides[activeSlide].type === "image" ? (
                  <div className="flex items-center justify-center t h-40 border rounded-xl bg-gray-50 text-gray-500 text-base font-medium">
                    No Question Available For Editing
                  </div>
                ) : slides[activeSlide].type === "blank" ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={
                          slidesAnimationData[activeSlide]
                            ?.canvasBackgroundColor || "#1a1a2e"
                        }
                        onChange={(e) => {
                          const color = e.target.value;
                          setSlidesAnimationData((prev) => ({
                            ...prev,
                            [activeSlide]: {
                              ...(prev[activeSlide] || {}),
                              canvasBackgroundColor: color,
                            },
                          }));
                        }}
                        className="w-10 h-10 rounded border border-gray-200 cursor-pointer p-0.5"
                      />
                      <input
                        type="text"
                        value={
                          slidesAnimationData[activeSlide]
                            ?.canvasBackgroundColor || "#1a1a2e"
                        }
                        onChange={(e) => {
                          const color = e.target.value;
                          setSlidesAnimationData((prev) => ({
                            ...prev,
                            [activeSlide]: {
                              ...(prev[activeSlide] || {}),
                              canvasBackgroundColor: color,
                            },
                          }));
                        }}
                        className="border rounded-lg p-2 text-sm w-32"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* 📝 Question */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question
                      </label>
                      <input
                        type="text"
                        readOnly={isReorderOnly}
                        value={slides[activeSlide].question.question_text || ""}
                        onChange={(e) => {
                          const updatedSlides = [...slides];
                          updatedSlides[activeSlide].question.question_text =
                            e.target.value;
                          setSlides(updatedSlides);
                        }}
                        className={`w-full border rounded-lg p-2 text-sm ${isReorderOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
                      />
                    </div>

                    {/* 🗳️ Choices for Poll or Multiple */}
                    {(slides[activeSlide].question.question_type === "poll" ||
                      slides[activeSlide].question.question_type ===
                        "multiple") && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Choices
                        </label>
                        {slides[activeSlide].question.choices?.map(
                          (choice, i) => (
                            <div
                              key={i}
                              className="flex items-center space-x-2 mb-2"
                            >
                              <input
                                type="text"
                                readOnly={isReorderOnly}
                                value={choice.choice_text || choice}
                                onChange={(e) => {
                                  const updatedSlides = [...slides];
                                  const newChoices = [
                                    ...updatedSlides[activeSlide].choices,
                                  ];
                                  newChoices[i] = e.target.value;
                                  updatedSlides[activeSlide].choices =
                                    newChoices;
                                  setSlides(updatedSlides);
                                }}
                                className={`w-full border rounded-lg p-2 text-sm ${isReorderOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              />
                              {!isReorderOnly && (
                                <button
                                  onClick={() => {
                                    const updatedSlides = [...slides];
                                    updatedSlides[
                                      activeSlide
                                    ].question.choices = updatedSlides[
                                      activeSlide
                                    ].question.choices.filter(
                                      (_, idx) => idx !== i,
                                    );
                                    setSlides(updatedSlides);
                                  }}
                                  className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ),
                        )}

                        {!isReorderOnly && (
                          <button
                            onClick={() => {
                              const updatedSlides = [...slides];
                              const newChoices = [
                                ...(updatedSlides[activeSlide].question
                                  .choices || []),
                                "",
                              ];
                              updatedSlides[activeSlide].question.choices =
                                newChoices;
                              setSlides(updatedSlides);
                            }}
                            className="mt-2 px-3 py-2 bg-lightPurple text-white rounded-lg text-sm hover:bg-purpleColor"
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-1" />
                            Add Choice
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-end my-5 border-t border-slate-300 pt-4">
          <button
            onClick={handleUpdatePresentation}
            disabled={updatePresentationLoading}
            className={`px-4 py-2 bg-lightPurple text-white rounded-md transition font-medium ${
              updatePresentationLoading
                ? "cursor-not-allowed bg-lightPurple/60"
                : "hover:bg-purpleColor"
            }`}
          >
            {updatePresentationLoading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin mr-2"
                />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>

        <ExportPresentation slides={slides} getLinks={getLinks} />
      </div>
    </div>
  );
};

export default EditRightPanel;
